import React, { useEffect, useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Grow,
  alpha,
  useScrollTrigger,
  Slide,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Mic as PodcastIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { isAuthenticated } from "common/utilities";
import { useMediaQuery, useTheme } from "@mui/material";
import logo from "assets/logo.jpeg";
import PodcastModal from "components/podcast";

// Custom hook for elevation scroll
const useElevationScroll = () => {
  const theme = useTheme();
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  return {
    elevation: trigger ? 4 : 0,
    sx: {
      transform: "translateY(0)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      backdropFilter: trigger ? "blur(10px)" : "none",
      backgroundColor: trigger
        ? alpha(theme.palette.background.paper, 0.95)
        : alpha(theme.palette.background.paper, 0.98),
    },
  };
};

// Animated button styles
const createButtonStyles = (isActive, theme) => ({
  color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
  fontSize: {
    xs: "0.75rem",
    sm: "0.8125rem",
    md: "0.875rem",
  },
  fontWeight: 500,
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  position: "relative",
  padding: "8px 16px",
  borderRadius: "8px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  minWidth: "auto",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: "50%",
    width: isActive ? "100%" : "0%",
    height: "2px",
    backgroundColor: theme.palette.primary.main,
    transform: "translateX(-50%)",
    transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  "&:hover": {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-2px)",
    "&::before": {
      width: "100%",
    },
  },
  "&:active": {
    transform: "translateY(0)",
  },
});

const Header = ({ aboutRef, servicesRef, teamsRef, storyRef, contactsRef }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [podcastModalOpen, setPodcastModalOpen] = useState(false);
  const [activeButton, setActiveButton] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const headerRef = useRef(null);

  const elevationProps = useElevationScroll();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Sections with icons for better UX
  const sections = [
    { ref: aboutRef, label: "About" },
    { ref: storyRef, label: "Why Us" },
    { ref: teamsRef, label: "Our Team" },
    { ref: servicesRef, label: "Services" },
    { ref: contactsRef, label: "Contact" },
  ];

  // Handle scroll for header elevation and active section
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show elevation effect when scrolled
      setIsScrolled(currentScrollY > 20);

      // Show scroll to top button
      setShowScrollTop(currentScrollY > 400);

      // Detect active section
      let currentActiveLabel = "";
      const windowHeight = window.innerHeight;
      const scrollPosition = currentScrollY + 100;

      sections.forEach((section) => {
        if (section.ref.current) {
          const element = section.ref.current;
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top + scrollPosition;
          const elementBottom = elementTop + rect.height;

          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            currentActiveLabel = section.label;
          }
        }
      });

      if (scrollPosition < windowHeight * 0.5) {
        setActiveButton("");
      } else if (currentActiveLabel) {
        setActiveButton(currentActiveLabel);
      }
    };

    // Add throttle to scroll handler for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", throttledScroll);
  }, [sections]);

  // Navigation handlers
  const scrollToSection = (ref, label) => {
    if (ref?.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setActiveButton(label);
      setDrawerOpen(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveButton("");
  };

  const navigateWithAnimation = (path, label) => {
    // Scroll to top before navigation
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveButton(label);

    // Add a small delay to ensure smooth scroll completes
    setTimeout(() => {
      navigate(path);
    }, 300);

    setDrawerOpen(false);
  };

  // Career navigation function with scroll to top
  const handleCareerClick = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveButton("Career");

    setTimeout(() => {
      navigate("/caregiver/new");
    }, 300);

    setDrawerOpen(false);
  };

  // Login navigation function with scroll to top
  const handleLoginClick = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveButton("Login");

    setTimeout(() => {
      navigate("/login");
    }, 300);

    setDrawerOpen(false);
  };

  // Dashboard navigation function with scroll to top
  const handleDashboardClick = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    setActiveButton("Dashboard");

    setTimeout(() => {
      navigate("/dashboard");
    }, 300);

    setDrawerOpen(false);
  };

  // Drawer content
  const drawerContent = (
    <Box
      sx={{
        width: 280,
        height: "100%",
        background: theme.palette.background.paper,
      }}
      role="presentation"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="AtHomeGeriCare"
          sx={{
            height: 40,
            cursor: "pointer",
            transition: "transform 0.3s",
            "&:hover": {
              transform: "scale(1.05)",
            },
          }}
          onClick={scrollToTop}
        />
        <IconButton
          onClick={() => setDrawerOpen(false)}
          sx={{
            color: theme.palette.text.primary,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              transform: "rotate(90deg)",
              transition: "transform 0.3s",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List sx={{ p: 2 }}>
        {sections.map((section) => (
          <ListItem
            key={section.label}
            onClick={() => scrollToSection(section.ref, section.label)}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                transform: "translateX(4px)",
              },
              ...(activeButton === section.label && {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                borderLeft: `4px solid ${theme.palette.primary.main}`,
              }),
            }}
          >
            <ListItemText
              primary={section.label}
              primaryTypographyProps={{
                fontWeight: activeButton === section.label ? 600 : 400,
                color:
                  activeButton === section.label ? "primary" : "textPrimary",
              }}
            />
          </ListItem>
        ))}

        <ListItem
          onClick={() => {
            setPodcastModalOpen(true);
            setDrawerOpen(false);
          }}
          sx={{
            borderRadius: 2,
            mb: 1,
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: alpha(theme.palette.secondary.main, 0.08),
              transform: "translateX(4px)",
            },
          }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PodcastIcon fontSize="small" />
                Podcast
              </Box>
            }
          />
        </ListItem>

        <ListItem
          onClick={handleCareerClick}
          sx={{
            borderRadius: 2,
            mb: 1,
            transition: "all 0.2s",
            "&:hover": {
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              transform: "translateX(4px)",
            },
          }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WorkIcon fontSize="small" />
                Career
              </Box>
            }
          />
        </ListItem>

        {isAuthenticated() ? (
          <ListItem
            onClick={handleDashboardClick}
            sx={{
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemText
              primary="Dashboard"
              primaryTypographyProps={{
                color: "primary",
                fontWeight: 600,
              }}
            />
          </ListItem>
        ) : (
          <ListItem
            onClick={handleLoginClick}
            sx={{
              borderRadius: 2,
              mb: 1,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: alpha(theme.palette.info.main, 0.08),
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonIcon fontSize="small" />
                  Login
                </Box>
              }
            />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        ref={headerRef}
        position="fixed"
        elevation={elevationProps.elevation}
        sx={{
          ...elevationProps.sx,
          background: isScrolled
            ? `linear-gradient(135deg, 
                ${alpha(theme.palette.background.paper, 0.97)} 0%, 
                ${alpha(theme.palette.background.paper, 0.95)} 100%)`
            : alpha(theme.palette.background.paper, 1),
          borderBottom: isScrolled
            ? `1px solid ${alpha(theme.palette.divider, 0.1)}`
            : "none",
          boxShadow: isScrolled
            ? `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`
            : "none",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          height: { xs: 64, sm: 70, md: 76 },
          transform: "translateY(0) !important",
          opacity: 1,
        }}
      >
        <Toolbar
          sx={{
            height: "100%",
            px: { xs: 2, sm: 3, md: 4 },
            justifyContent: "space-between",
          }}
        >
          {/* Logo */}
          <Grow in timeout={600}>
            <Box
              component="img"
              src={logo}
              alt="AtHomeGeriCare"
              onClick={scrollToTop}
              sx={{
                height: { xs: 40, sm: 45, md: 50 },
                cursor: "pointer",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            />
          </Grow>

          {/* Desktop Navigation - Always Visible */}
          {!isMobile ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5, md: 2 },
                flex: 1,
                justifyContent: "center",
                ml: 4,
              }}
            >
              {sections.map((section) => (
                <Button
                  key={section.label}
                  color="inherit"
                  onClick={() => scrollToSection(section.ref, section.label)}
                  sx={createButtonStyles(activeButton === section.label, theme)}
                >
                  {section.label}
                </Button>
              ))}

              {/* Podcast Button */}
              <Button
                color="inherit"
                onClick={() => setPodcastModalOpen(true)}
                sx={createButtonStyles(activeButton === "Podcast", theme)}
                startIcon={<PodcastIcon fontSize="small" />}
              >
                Podcast
              </Button>

              {/* Career Button */}
              <Button
                color="inherit"
                onClick={handleCareerClick}
                sx={createButtonStyles(activeButton === "Career", theme)}
                startIcon={<WorkIcon fontSize="small" />}
              >
                Career
              </Button>
            </Box>
          ) : null}

          {/* Right side buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5, md: 2 },
            }}
          >
            {!isMobile ? (
              <>
                {isAuthenticated() ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDashboardClick}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 6px 20px ${alpha(
                          theme.palette.primary.main,
                          0.3
                        )}`,
                      },
                    }}
                  >
                    Dashboard
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleLoginClick}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      fontWeight: 500,
                      textTransform: "none",
                      fontSize: "0.9375rem",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.04
                        ),
                      },
                    }}
                    startIcon={<PersonIcon />}
                  >
                    Login
                  </Button>
                )}
              </>
            ) : (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  color: theme.palette.text.primary,
                  transition: "all 0.3s",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    transform: "scale(1.1)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Scroll to Top Button */}
      <Slide direction="up" in={showScrollTop} mountOnEnter unmountOnExit>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1000,
            backgroundColor: theme.palette.primary.main,
            color: "white",
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              transform: "translateY(-4px)",
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            transition: "all 0.3s",
          }}
        >
          <ExpandLessIcon />
        </IconButton>
      </Slide>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Podcast Modal */}
      <PodcastModal
        open={podcastModalOpen}
        onClose={() => setPodcastModalOpen(false)}
      />
    </>
  );
};

export default Header;
// import React, { useEffect, useState, useMemo } from "react";

// import PodcastModal from "../components/podcast";

// import {
//   AppBar,
//   Toolbar,
//   Box,
//   Button,
//   IconButton,
//   Drawer,
//   List,
//   ListItem,
//   ListItemText,
// } from "@mui/material";
// import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
// import { useNavigate } from "react-router-dom";
// import { isAuthenticated } from "common/utilities";
// import { useMediaQuery } from "@mui/material";
// import logo from "assets/slog.png";

// const buttonStyles = (isActive) => ({
//   color: isActive ? "orange" : "grey",
//   fontSize: {
//     sx: 14,
//     xl: 20,
//   },
//   fontWeight: 100,
//   fontFamily: "Arial, Helvetica, sans-serif",
//   borderBottom: isActive ? "2px solid orange" : "none",
//   borderRadius: 0,
// });

// const Header = ({ aboutRef, servicesRef, teamsRef, storyRef, contactsRef }) => {
//   const navigate = useNavigate();

//   const [drawerOpen, setDrawerOpen] = useState(false);
//   const [podcastOpen, setPodcastOpen] = useState(false);
//   const [isScrolled, setIsScrolled] = useState(false);
//   const [activeButton, setActiveButton] = useState("");

//   const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("lg"));

//   const sections = useMemo(
//     () => [
//       { ref: aboutRef, label: "About" },
//       { ref: storyRef, label: "Why Choose Us" },
//       { ref: teamsRef, label: "Meet the Minds" },
//       { ref: servicesRef, label: "Services" },
//       { ref: contactsRef, label: "Contact" },
//     ],
//     [aboutRef, storyRef, teamsRef, servicesRef, contactsRef]
//   );

//   const scrollToTop = () => {
//     window.scrollTo({ top: 0, behavior: "smooth" });
//     setActiveButton("");
//   };

//   useEffect(() => {
//     const handleScroll = () => {
//       const scrollPosition = window.scrollY;
//       const windowHeight = window.innerHeight;

//       let currentActiveLabel = "";

//       sections.forEach((section) => {
//         if (section.ref?.current) {
//           const rect = section.ref.current.getBoundingClientRect();
//           if (rect.top < windowHeight && rect.bottom >= 0) {
//             currentActiveLabel = section.label;
//           }
//         }
//       });

//       if (scrollPosition < windowHeight * 0.3) {
//         setActiveButton("");
//       } else if (currentActiveLabel) {
//         setActiveButton(currentActiveLabel);
//       }

//       setIsScrolled(scrollPosition > window.innerHeight * 0.2);
//     };

//     window.addEventListener("scroll", handleScroll);
//     handleScroll();

//     return () => window.removeEventListener("scroll", handleScroll);
//   }, [sections]);

//   const gotoAbout = () => {
//     aboutRef.current.scrollIntoView({ behavior: "smooth" });
//     setActiveButton("About");
//   };

//   const gotoStory = () => {
//     storyRef.current.scrollIntoView({ behavior: "smooth" });
//     setActiveButton("Why Choose Us");
//   };

//   const gotoTeam = () => {
//     teamsRef.current.scrollIntoView({ behavior: "smooth" });
//     setActiveButton("Meet the Minds");
//   };

//   const gotoServices = () => {
//     servicesRef.current.scrollIntoView({ behavior: "smooth" });
//     setActiveButton("Services");
//   };

//   const gotoContact = () => {
//     contactsRef.current.scrollIntoView({ behavior: "smooth" });
//     setActiveButton("Contact");
//   };

//   const handleApply = () => {
//     setActiveButton("Join");
//     navigate("/caregiver/new");
//   };
//   const login = () => {
//     navigate("/login");
//     setActiveButton("Login");
//   };

//   const handlePodcastClick = () => {
//     setPodcastOpen(true);
//     setActiveButton("Podcast");
//   };

//   const drawerContent = (
//     <Box sx={{ width: 250 }} role="presentation">
//       <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
//         <IconButton onClick={() => setDrawerOpen(false)}>
//           <CloseIcon />
//         </IconButton>
//       </Box>
//       <List>
//         <ListItem onClick={gotoAbout}>
//           <ListItemText primary="About" />
//         </ListItem>
//         <ListItem onClick={gotoStory}>
//           <ListItemText primary="Why Choose Us" />
//         </ListItem>
//         <ListItem onClick={gotoTeam}>
//           <ListItemText primary="Meet the Minds" />
//         </ListItem>
//         <ListItem onClick={gotoServices}>
//           <ListItemText primary="Services" />
//         </ListItem>
//         <ListItem onClick={gotoContact}>
//           <ListItemText primary="Contact" />
//         </ListItem>
//         <ListItem onClick={handleApply}>
//           <ListItemText primary="Career" />
//         </ListItem>
//         <ListItem onClick={handlePodcastClick}>
//           <ListItemText primary="Podcast" />
//         </ListItem>
//         {isAuthenticated() ? (
//           <ListItem onClick={() => navigate("/dashboard")}>
//             <ListItemText primary="Dashboard" />
//           </ListItem>
//         ) : (
//           <ListItem onClick={login}>
//             <ListItemText primary="Login" />
//           </ListItem>
//         )}
//       </List>
//     </Box>
//   );

//   return (
//     <>
//       <AppBar
//         position="fixed"
//         sx={{
//           backgroundColor: "white",
//           boxShadow: "none",
//           height: isScrolled ? 64 : 80,
//         }}
//       >
//         <Toolbar
//           sx={{ justifyContent: isSmallScreen ? "flex-start" : "flex-end" }}
//         >
//           {isSmallScreen ? (
//             <>
//               <Box
//                 component="img"
//                 src={logo}
//                 alt="logo"
//                 sx={{ height: 60, cursor: "pointer" }}
//                 onClick={scrollToTop}
//               />
//               <IconButton
//                 sx={{ position: "absolute", right: 10 }}
//                 onClick={() => setDrawerOpen(true)}
//               >
//                 <MenuIcon />
//               </IconButton>
//               <Drawer
//                 anchor="right"
//                 open={drawerOpen}
//                 onClose={() => setDrawerOpen(false)}
//               >
//                 {drawerContent}
//               </Drawer>
//             </>
//           ) : (
//             <>
//               <Box
//                 component="img"
//                 src={logo}
//                 alt="logo"
//                 onClick={scrollToTop}
//                 sx={{
//                   position: "absolute",
//                   left: 20,
//                   height: 80,
//                   cursor: "pointer",
//                 }}
//               />

//               <Box
//                 sx={{
//                   width: "65%",
//                   display: "flex",
//                   justifyContent: "space-between",
//                 }}
//               >
//                 <Button
//                   onClick={gotoAbout}
//                   sx={buttonStyles(activeButton === "About")}
//                 >
//                   About
//                 </Button>
//                 <Button
//                   onClick={gotoStory}
//                   sx={buttonStyles(activeButton === "Why Choose Us")}
//                 >
//                   Why Choose Us
//                 </Button>
//                 <Button
//                   onClick={gotoTeam}
//                   sx={buttonStyles(activeButton === "Meet the Minds")}
//                 >
//                   Meet the Minds
//                 </Button>
//                 <Button
//                   onClick={gotoServices}
//                   sx={buttonStyles(activeButton === "Services")}
//                 >
//                   Services
//                 </Button>
//                 <Button
//                   onClick={gotoContact}
//                   sx={buttonStyles(activeButton === "Contact")}
//                 >
//                   Contact
//                 </Button>
//                 <Button
//                   onClick={handleApply}
//                   sx={buttonStyles(activeButton === "Join")}
//                 >
//                   Career
//                 </Button>
//                 <Button
//                   onClick={handlePodcastClick}
//                   sx={buttonStyles(activeButton === "Podcast")}
//                 >
//                   Podcast
//                 </Button>

//                 {isAuthenticated() ? (
//                   <Button
//                     variant="contained"
//                     color="warning"
//                     onClick={() => navigate("/dashboard")}
//                   >
//                     Dashboard
//                   </Button>
//                 ) : (
//                   <Button
//                     onClick={login}
//                     sx={buttonStyles(activeButton === "Login")}
//                   >
//                     Login
//                   </Button>
//                 )}
//               </Box>
//             </>
//           )}
//         </Toolbar>
//       </AppBar>

//       {/* Podcast Modal */}
//       <PodcastModal open={podcastOpen} onClose={() => setPodcastOpen(false)} />
//     </>
//   );
// };

// export default Header;
